import { useCallback, useEffect, useRef, useState } from "react";
import { FiLoader, FiSend, FiMessageSquare, FiPlus, FiX, FiSearch } from "react-icons/fi";
import { useSocket } from "../../context/SocketContext";
import { feedRequest } from "./api";
import useFeedPages from "./useFeedPages";
import FeedPost from "./FeedPost";

export default function GroupFeed({ groupId }) {
  const { socket } = useSocket();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const activeQuery = useRef(query);
  activeQuery.current = query;
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  const page = useFeedPages(`/groups/${groupId}/posts${query ? `?q=${encodeURIComponent(query)}` : ""}`, revision);
  const [commentRevisions, setCommentRevisions] = useState({});
  const { updateItem, remove, more } = page;
  const sentinelRef = useRef(null);
  const loadedIds = useRef(new Set());
  useEffect(() => { loadedIds.current = new Set(page.items.map(post => post._id)); }, [page.items]);
  const updatePost = useCallback((postId, kind = "post-edited") => {
    if (kind === "post-deleted") remove(postId);
    else if (kind === "post-created" || loadedIds.current.has(postId) || (query && kind === "post-edited")) updateItem(postId, async () => {
      const { item } = await feedRequest(`/posts/${postId}`);
      return !activeQuery.current || item.body.toLowerCase().includes(activeQuery.current.toLowerCase()) ? item : null;
    });
    if (kind.startsWith("comment-")) setCommentRevisions(previous => ({ ...previous, [postId]: (previous[postId] || 0) + 1 }));
  }, [updateItem, remove, query]);
  useEffect(() => {
    if (!page.nextCursor || page.loading || page.loadingMore || page.refreshing || page.error) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) more();
    }, { rootMargin: "200px" });
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [page.nextCursor, page.loading, page.loadingMore, page.refreshing, page.error, more]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const dialogRef = useRef(null);
  useEffect(() => {
    if (!composerOpen) return;
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    dialog.showModal();
    return () => { dialog.close(); previousFocus?.focus(); };
  }, [composerOpen]);
  useEffect(() => {
    const timers = new Map();
    const changed = event => {
      if (event.groupId !== String(groupId)) return;
      if (!event.postId) return;
      clearTimeout(timers.get(event.postId));
      timers.set(event.postId, setTimeout(() => { timers.delete(event.postId); updatePost(event.postId, event.kind); }, 150));
    };
    socket?.on("feed:updated", changed);
    socket?.on("connect", refresh);
    return () => {
      timers.forEach(clearTimeout);
      socket?.off("feed:updated", changed);
      socket?.off("connect", refresh);
    };
  }, [socket, groupId, refresh, updatePost]);
  const submit = async event => {
    event.preventDefault();
    if (!body.trim() || busy) return;
    setBusy(true); setError("");
    try { const data = await feedRequest(`/groups/${groupId}/posts`, { method: "POST", body: JSON.stringify({ body }) }); setBody(""); setComposerOpen(false); updatePost(data.id, "post-created"); }
    catch (error) { setError(error.message); }
    finally { setBusy(false); }
  };
  return <>
    <aside className="min-w-0"><button type="button" onClick={() => { setError(""); setComposerOpen(true); }} className="flex h-10 w-full items-center justify-center gap-2 rounded-md bg-orange-700 px-4 text-sm font-semibold text-white hover:bg-orange-800"><FiPlus aria-hidden="true" />Create post</button></aside>
    <div className="min-w-0 lg:border-l lg:border-gray-200 lg:pl-6">
    {(page.items.length > 0 || search || query) && <label className="mb-5 flex h-10 min-w-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-3">
      <FiSearch aria-hidden="true" className="shrink-0 text-gray-600" />
      <input type="search" aria-label="Search feed" placeholder="Search feed" maxLength={200} value={search} onChange={event => setSearch(event.target.value)} className="min-w-0 flex-1 bg-white text-sm outline-none" />
      {search && <button type="button" aria-label="Clear feed search" title="Clear search" onClick={() => { setSearch(""); setQuery(""); }} className="flex h-8 w-8 shrink-0 items-center justify-center text-gray-600"><FiX aria-hidden="true" /></button>}
    </label>}
    <dialog ref={dialogRef} aria-labelledby="create-post-title" onCancel={event => { event.preventDefault(); if (!busy) setComposerOpen(false); }}
      className="m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-lg overflow-y-auto rounded-lg border border-gray-200 bg-white p-5 text-gray-900 shadow-xl backdrop:bg-black/40 sm:p-6">
    <div className="mb-5 flex items-center justify-between gap-3"><h2 id="create-post-title" className="text-lg font-semibold">Create post</h2><button type="button" title="Close" aria-label="Close post popup" disabled={busy} onClick={() => setComposerOpen(false)} className="flex h-9 w-9 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed"><FiX aria-hidden="true" /></button></div>
    <form onSubmit={submit}>
      <label htmlFor="new-feed-post" className="mb-2 block text-sm font-medium">Share with your group</label>
      <textarea autoFocus id="new-feed-post" placeholder="What's on your mind?" required maxLength={5000} rows={6} disabled={busy} value={body} onChange={event => setBody(event.target.value)}
        className="block w-full resize-y rounded-md border border-gray-300 bg-white p-3 text-sm focus:border-orange-600 focus:outline-none" />
      <div className="mt-3 flex items-center justify-between gap-3"><span className="text-xs text-gray-500">{body.length}/5,000</span><button disabled={busy || !body.trim()} aria-busy={busy} className="flex h-10 min-w-24 items-center justify-center gap-2 rounded-md bg-orange-700 px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"><FiSend aria-hidden="true" />Post</button></div>
      {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
    </form>
    </dialog>
    {page.error && <p role="alert" className="mb-4 text-sm text-red-700">{page.error} <button onClick={page.retry} className="underline">Retry feed</button></p>}
    <div className="space-y-5">{page.items.map(post => <FeedPost key={post._id} post={post} revision={`${revision}:${commentRevisions[post._id] || 0}`} onChange={kind => updatePost(post._id, kind)} />)}</div>
    {page.loading && <div role="status" aria-label="Loading feed" className="flex justify-center py-6"><FiLoader className="h-5 w-5 animate-spin text-orange-700" /></div>}
    {!page.loading && !page.error && !page.items.length && <div className="py-12 text-center"><FiMessageSquare aria-hidden="true" className="mx-auto mb-3 h-7 w-7 text-orange-700" /><h3 className="text-base font-semibold">{query ? "No matching posts" : "No posts yet"}</h3>{!query && <p className="mt-2 text-sm text-gray-600">Be the first to share something.</p>}</div>}
    {page.nextCursor && <div ref={sentinelRef} className="h-12" aria-label="More posts" />}
    {page.loadingMore && <p role="status" className="py-3 text-center text-xs text-gray-600">Loading older posts...</p>}
    </div>
  </>;
}
